import {assert} from 'Asserts';
import {makeReactContext} from 'lib/makeReactContext';

import {PipelineEdgeUpsertById} from '../network/PipelineDAO';
import {PipelineGraphQL} from '../PipelineTypes';
import {ConfigDataFrameInfo} from './StepConfigEditorTypes';

const EMPTY_DATA_FRAME_INFO: ConfigDataFrameInfo = {dataFrameSpec: {name: 'Input dataset', columns: []}};

interface StepConfigEditorContextValue {
  // Usually the pipelineId, but for the TransformPopover in the flowchart its the step id because it is hard
  // to get the pipelineId, and the stepId works because you can't have the popover open while changing the
  // stepId.
  clientStateId: string;
  /**
   * Mapping of input step data names to data frame specs.
   *
   * Input step data names are part of the step config shape. See `inputDataName` in `PipelineConfigJsonSpec`.
   *
   * This mapping can be created from an existing `PipelineStep` with `getInputStepDatasRecord`.
   */
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>;
  /**
   * Mapping from action keys to the action callbacks.
   */
  actions?: Record<string, () => void>;
  /**
   * The pipeline step being edited. Only populated where a reliable step id is available; undefined
   * otherwise.
   * Consumed by the Custom Code editor to launch a Pipelines Custom Code Writer chat targeting the step.
   */
  pipelineStepId?: string;
  /**
   * The pipeline topology needed by editors that read steps, views, and edges
   * (e.g. inline source selection). Provided when the editor is rendered inside the analysis sidebar.
   */
  pipeline?: Pick<PipelineGraphQL, 'id' | 'steps' | 'views'>;
  /**
   * Callback to update the step's incoming edges as draft state. The save boundary persists
   * these atomically with config. Editors that change DAG topology (e.g. input source selectors)
   * call this instead of mutating the pipeline directly.
   */
  onChangeIncomingEdges?: (edges: Array<PipelineEdgeUpsertById>) => void;
}

export const StepConfigEditorContext = makeReactContext<StepConfigEditorContextValue>(() => {
  assert(false, 'StepConfigEditorContext not initialized');
}, 'StepConfigEditorContext');

export function useStepConfigEditorContext(): StepConfigEditorContextValue {
  return StepConfigEditorContext.useContext();
}

/**
 * Get a data frame info or, if no input data frame is present, an empty info.
 *
 * Default to an empty info, rather than raising an exception, because an editor may be rendered
 * (in read-only) even if there is missing input, so the user can view the config.
 *
 * @param inputDataFrames Mapping of input step data names to data frame specs.
 * @oaram inputDataName Key of inputDataFrames
 */
export function getDataFrameInfoOrEmptyInfo(
  dataFramesInfos: Record<string, Array<ConfigDataFrameInfo>>,
  inputDataName: string,
  inputDataIndex: number = 0
): ConfigDataFrameInfo {
  return dataFramesInfos[inputDataName] && dataFramesInfos[inputDataName].length > inputDataIndex
    ? dataFramesInfos[inputDataName][inputDataIndex]
    : EMPTY_DATA_FRAME_INFO;
}
